export interface ScanTestResultHistoryEntry {
  testTime: number;
  result: string;
  message: string;
}

export interface ScanTestGateEntry {
  expected: string;
  lastResult: string;
  resultHistory: ScanTestResultHistoryEntry[];
}

export type ScanTestGateArray = Record<string, ScanTestGateEntry>;

export type RunStatus = 'queued' | 'running' | 'complete' | 'failed';

export interface CurrentRun {
  runId: string;
  status: RunStatus;
  startedAt: number;
  startedBy?: string;
  runningAt?: number;
  finishedAt?: number;
  error?: string;
  item?: number;
  totalTests?: number;
  completedTests?: number;
  progressPercent?: number;
}

export interface RunTestStartResponse {
  runId: string;
  status: RunStatus;
  key: string;
  startedAt: number;
  item?: number;
}

export type RunTestStatusResponse = CurrentRun;

export interface GateGroup {
  id: string;
  name: string;
  gateNames: string[];
}

// A reference to a draggable rule item: either a whole event, or a single plan
// within an event. `plan` is present only when `kind === 'plan'`.
export interface RuleItemRef {
  kind: 'event' | 'plan';
  eventName: string;
  plan?: string;
}

// A scan window on a gate. Offsets are minutes relative to EP (event) time;
// negative means before EP time (e.g. -720 = 12h before). Retained only as the
// source shape for migrating legacy scan rules into the typed EntryRule model.
export interface ScanWindow {
  id: string;
  openOffsetMins: number;
  closeOffsetMins: number;
  items: RuleItemRef[];
}

// ─── Typed entry rules ──────────────────────────────────────────────────────
// A gate's access is authored as a list of typed rules. Each rule narrows what
// may scan at that gate along one dimension (time, event/plan, ticket locator).

// When the gate's scan period opens and closes. `offset` mode measures minutes
// relative to EP (event) time (negative = before); `specific` mode uses Unix
// epoch seconds. Stored config only — does not affect static expected Y/N.
export interface ScanPeriodRule {
  type: 'scan_period';
  mode: 'offset' | 'specific';
  openValue: number;
  closeValue: number;
}

// The events/plans permitted to scan at this gate. Drives expected Y/N.
export interface EventsRule {
  type: 'events';
  items: RuleItemRef[];
}

// References a reusable AccessGroup (a named event/plan bundle) on the master
// config. Resolved to its items when computing expected Y/N.
export interface AccessGroupRule {
  type: 'access_group';
  groupId: string;
}

// Restrict to given sections (and optionally specific rows within them). An
// empty `rows` array means the whole section. Seeds extended test rows.
export interface SectionRowRule {
  type: 'section_row';
  selections: Array<{ section: string; rows: string[] }>;
  // Stable id minted once when the rule is created; used to key the extended
  // seed rows it generates so re-saving does not duplicate them.
  seedKey: string;
}

// Ticket usage designation (single/multi-day, one-time/unlimited entry). Seeds
// an extended test row; does not itself flip existing barcodes' expected Y/N.
export interface TicketUsageRule {
  type: 'ticket_usage';
  dayUsage: 'single' | 'multi';
  entryUsage: 'one_time' | 'unlimited';
  seedKey: string;
}

// Restrict to given price codes. Seeds one extended test row per price code.
export interface PriceCodeRule {
  type: 'price_code';
  priceCodes: string[];
  seedKey: string;
}

export type EntryRule =
  | ScanPeriodRule
  | EventsRule
  | AccessGroupRule
  | SectionRowRule
  | TicketUsageRule
  | PriceCodeRule;

// The scan rules for a single gate. Rules are grouped: within a group they
// combine with AND (shown joined by "+"); groups on a gate combine with OR. A
// barcode is accepted at the gate when at least one group accepts it. A gate
// with no groups (or only empty groups) accepts nothing.
export interface GateRule {
  gate: string;
  groups: EntryRule[][];
}

// The pre-grouping typed shape (a flat rule list). Kept only as a source type
// for the lazy migration into `groups` performed on load.
export interface FlatGateRule {
  gate: string;
  rules: EntryRule[];
}

// The legacy scan-window shape, kept only as the source type for migration.
export interface LegacyGateRule {
  gate: string;
  windows: ScanWindow[];
}

// A user-defined, reusable named bundle of events/plans. Dropping an
// AccessGroupRule onto a gate is equivalent to an EventsRule containing all of
// this group's items. Stored on ScanTestMasterConfig.accessGroups.
export interface AccessGroup {
  id: string;
  name: string;
  items: RuleItemRef[];
}

export interface ScanTestMasterConfig {
  key: string;
  item: 0;
  schedule?: number[];
  timezone?: string;
  notificationEmails?: string[];
  ttl?: number;
  currentRun?: CurrentRun;
  gateGroups?: GateGroup[];
  accessGroups?: AccessGroup[];
  scanRules?: GateRule[];
}

export interface ScanTestBarcode {
  key: string;
  item: number;
  barcode: string;
  gateArray: ScanTestGateArray;
  ttl?: number;
  database_name?: string;
  event_name?: string;
  plan_event_name?: string;
  fantm_event_name?: string;
  tm_event_name?: string;
  description?: string;
  price_code?: string;
  retail_event_id?: string;
  num_seats?: string | number;
  plan?: string;
  entitlement?: string;
  host_ticket_type?: string;
  section?: string;
  row?: string;
  seat?: string;
  extended?: boolean;
  // Stable composite key linking this extended row to the rule that seeded it.
  // Set by rule-driven seed generation; absent on manually-created rows.
  ruleSeedKey?: string;
  event_type?: 'archtics' | 'host';
  lastRunId?: string;
}

export type ScanTestRecord = ScanTestMasterConfig | ScanTestBarcode;

export function isMasterConfig(record: ScanTestRecord): record is ScanTestMasterConfig {
  return Number(record.item) === 0;
}

export function buildScanTestKey(venueID: string, performanceItem: number): string {
  return `${venueID}_${performanceItem}`;
}

export function buildDefaultGateArray(gateNames: string[], expected: string = 'Y'): ScanTestGateArray {
  const gateArray: ScanTestGateArray = {};
  for (const name of gateNames) {
    gateArray[name] = { expected, lastResult: '', resultHistory: [] };
  }
  return gateArray;
}

// Converts a legacy scan-window gate rule to the typed EntryRule shape. All
// window items are unioned into a single EventsRule; the first window's offsets
// become a ScanPeriodRule (offset mode). The rules form a single AND group.
// Used to load pre-refactor data.
export function migrateGateRule(legacy: LegacyGateRule): GateRule {
  const rules: EntryRule[] = [];
  const seen = new Set<string>();
  const items: RuleItemRef[] = [];
  let openMins: number | undefined;
  let closeMins: number | undefined;

  for (const window of legacy.windows || []) {
    if (openMins === undefined) {
      openMins = window.openOffsetMins;
      closeMins = window.closeOffsetMins;
    }
    for (const ref of window.items || []) {
      const key = ref.kind === 'plan'
        ? `plan:${ref.eventName}:${ref.plan}`
        : `event:${ref.eventName}`;
      if (seen.has(key)) { continue; }
      seen.add(key);
      items.push({ ...ref });
    }
  }

  if (openMins !== undefined && closeMins !== undefined) {
    rules.push({ type: 'scan_period', mode: 'offset', openValue: openMins, closeValue: closeMins });
  }
  if (items.length) {
    rules.push({ type: 'events', items });
  }

  return { gate: legacy.gate, groups: rules.length ? [rules] : [] };
}

// Lifts a pre-grouping flat gate rule (a single `rules` array) into the grouped
// shape by wrapping its rules in one AND group. Used to load data saved before
// OR groups existed.
export function liftFlatGateRule(flat: FlatGateRule): GateRule {
  const rules = flat.rules || [];
  return { gate: flat.gate, groups: rules.length ? [rules] : [] };
}
